import reduceReducers from "reduce-reducers";
import { combineReducers } from "redux";

import {
  EXPORT_P2P_IN_FILE,
  EXPORT_P2P_IN_REVERSAL,
  EXPORT_P2P_OUT_FILE,
  EXPORT_P2P_OUT_REVERSAL,
  P2P_FILE,
  P2P_REVERSAL,
  NLIS_HOLDINGS,
  SELL_FILE,
  SELL_REVERSAL,
  SIGHTING_FILE,
  TAKE_FILE,
  TAKE_REVERSAL,
} from "constants/actionTypes";
import { TransferStatus } from "constants/nlis";

import {
  apiModelOfflineCreateReducer,
  apiModelOfflineDeleteReducer,
  apiModelOfflineFetchReducer,
  apiModelReducer,
} from "lib/reducers";
import {
  deserializeExportP2PInFile,
  deserializeExportP2PInReversal,
  deserializeExportP2POutFile,
  deserializeExportP2POutReversal,
  deserializeP2PFile,
  deserializeP2PReversal,
  deserializeSellFile,
  deserializeSellReversal,
  deserializeSighting,
  deserializeTakeFile,
  deserializeTakeReversal,
} from "lib/serializers/nlis";
import { deserializeNlisHolding } from "lib/serializers/nlisHoldings";

const takeFileFetchBulk = apiModelReducer("takeFiles", TAKE_FILE.FETCH_BULK, {
  deserializer: deserializeTakeFile,
  includeOrder: false,
  clearOnRequest: true,
});

const takeFileFetch = apiModelReducer("takeFiles", TAKE_FILE.FETCH, {
  deserializer: deserializeTakeFile,
  includeOrder: false,
  clearOnRequest: false,
});

const takeFileOfflineCreate = apiModelOfflineCreateReducer(TAKE_FILE, {
  deserializer: deserializeTakeFile,
});

const takeFileOfflineDelete = apiModelOfflineDeleteReducer(TAKE_FILE);

const takeFiles = reduceReducers(
  takeFileFetchBulk,
  takeFileFetch,
  takeFileOfflineCreate,
  takeFileOfflineDelete,
);

const takeReversalsFetchBulk = apiModelReducer(
  "takeReversals",
  TAKE_REVERSAL.FETCH_BULK,
  {
    deserializer: deserializeTakeReversal,
    includeOrder: false,
    clearOnRequest: true,
  },
);

const takeReversalsFetch = apiModelReducer(
  "takeReversals",
  TAKE_REVERSAL.FETCH,
  {
    deserializer: deserializeTakeReversal,
    includeOrder: false,
    clearOnRequest: false,
  },
);

const takeReversalsOfflineCreate = apiModelOfflineCreateReducer(TAKE_REVERSAL, {
  deserializer: deserializeTakeReversal,
});

const takeReversals = reduceReducers(
  takeReversalsFetch,
  takeReversalsFetchBulk,
  takeReversalsOfflineCreate,
);

const sellFileFetchBulk = apiModelReducer("sellFiles", SELL_FILE.FETCH_BULK, {
  deserializer: deserializeSellFile,
  includeOrder: false,
  clearOnRequest: true,
});

const sellFileFetch = apiModelReducer("sellFiles", SELL_FILE.FETCH, {
  deserializer: deserializeSellFile,
  includeOrder: false,
  clearOnRequest: false,
});

const sellFileOfflineCreate = apiModelOfflineCreateReducer(SELL_FILE, {
  deserializer: deserializeSellFile,
});

const sellFileOfflineDelete = apiModelOfflineDeleteReducer(SELL_FILE, {
  deserializer: deserializeSellFile,
});

const sellFiles = reduceReducers(
  sellFileFetchBulk,
  sellFileFetch,
  sellFileOfflineCreate,
  sellFileOfflineDelete,
);

const sellReversalsFetchBulk = apiModelReducer(
  "sellReversals",
  SELL_REVERSAL.FETCH_BULK,
  {
    deserializer: deserializeSellReversal,
    includeOrder: false,
    clearOnRequest: true,
  },
);

const sellReversalsFetch = apiModelReducer(
  "sellReversals",
  SELL_REVERSAL.FETCH,
  {
    deserializer: deserializeSellReversal,
    includeOrder: false,
    clearOnRequest: false,
  },
);

const sellReversalsOfflineCreate = apiModelOfflineCreateReducer(SELL_REVERSAL, {
  deserializer: deserializeSellReversal,
});

const sellReversals = reduceReducers(
  sellReversalsFetch,
  sellReversalsFetchBulk,
  sellReversalsOfflineCreate,
);

const createSightingFile = apiModelOfflineCreateReducer(SIGHTING_FILE, {
  deserializer: deserializeSighting,
});

const fetchSightingFiles = apiModelOfflineFetchReducer(SIGHTING_FILE, {
  deserializer: deserializeSighting,
});

const sightingFiles = reduceReducers(createSightingFile, fetchSightingFiles);

const createP2PFiles = apiModelOfflineCreateReducer(P2P_FILE, {
  deserializer: deserializeP2PFile,
});

const deleteP2PFiles = apiModelOfflineDeleteReducer(P2P_FILE, {
  deserializer: deserializeP2PFile,
});
const fetchP2PFiles = apiModelOfflineFetchReducer(P2P_FILE, {
  deserializer: deserializeP2PFile,
});

// Flags the forward file as pending rollback when its reversal is
// created - shared by generic P2P and both Export P2P legs.
const buildReversalPendingRollbackReducer =
  reversalCreateRequestType => (state, action) => {
    switch (action.type) {
      case reversalCreateRequestType:
        return {
          ...state,
          byId: {
            ...state.byId,
            [action.payload.reversal_of]: {
              ...state.byId[action.payload.reversal_of],
              status: TransferStatus.PENDING_ROLLBACK,
            },
          },
        };
      default:
        return state;
    }
  };

const p2pFiles = reduceReducers(
  createP2PFiles,
  fetchP2PFiles,
  deleteP2PFiles,
  buildReversalPendingRollbackReducer(P2P_REVERSAL.CREATE.REQUEST),
);

const createP2PReversals = apiModelOfflineCreateReducer(P2P_REVERSAL, {
  deserializer: deserializeP2PReversal,
});

const deleteP2PReversals = apiModelOfflineDeleteReducer(P2P_REVERSAL, {
  deserializer: deserializeP2PReversal,
});
const fetchP2PReversals = apiModelOfflineFetchReducer(P2P_REVERSAL, {
  deserializer: deserializeP2PReversal,
});

const p2pReversals = reduceReducers(
  createP2PReversals,
  deleteP2PReversals,
  fetchP2PReversals,
);

const createExportP2PInFiles = apiModelOfflineCreateReducer(
  EXPORT_P2P_IN_FILE,
  {
    deserializer: deserializeExportP2PInFile,
  },
);
const deleteExportP2PInFiles = apiModelOfflineDeleteReducer(
  EXPORT_P2P_IN_FILE,
  {
    deserializer: deserializeExportP2PInFile,
  },
);
const fetchExportP2PInFiles = apiModelOfflineFetchReducer(EXPORT_P2P_IN_FILE, {
  deserializer: deserializeExportP2PInFile,
});

const exportP2PInFiles = reduceReducers(
  createExportP2PInFiles,
  fetchExportP2PInFiles,
  deleteExportP2PInFiles,
  buildReversalPendingRollbackReducer(EXPORT_P2P_IN_REVERSAL.CREATE.REQUEST),
);

const createExportP2PInReversals = apiModelOfflineCreateReducer(
  EXPORT_P2P_IN_REVERSAL,
  { deserializer: deserializeExportP2PInReversal },
);
const deleteExportP2PInReversals = apiModelOfflineDeleteReducer(
  EXPORT_P2P_IN_REVERSAL,
  { deserializer: deserializeExportP2PInReversal },
);
const fetchExportP2PInReversals = apiModelOfflineFetchReducer(
  EXPORT_P2P_IN_REVERSAL,
  { deserializer: deserializeExportP2PInReversal },
);

const exportP2PInReversals = reduceReducers(
  createExportP2PInReversals,
  deleteExportP2PInReversals,
  fetchExportP2PInReversals,
);

const createExportP2POutFiles = apiModelOfflineCreateReducer(
  EXPORT_P2P_OUT_FILE,
  { deserializer: deserializeExportP2POutFile },
);
const deleteExportP2POutFiles = apiModelOfflineDeleteReducer(
  EXPORT_P2P_OUT_FILE,
  { deserializer: deserializeExportP2POutFile },
);
const fetchExportP2POutFiles = apiModelOfflineFetchReducer(
  EXPORT_P2P_OUT_FILE,
  { deserializer: deserializeExportP2POutFile },
);

const exportP2POutFiles = reduceReducers(
  createExportP2POutFiles,
  fetchExportP2POutFiles,
  deleteExportP2POutFiles,
  buildReversalPendingRollbackReducer(EXPORT_P2P_OUT_REVERSAL.CREATE.REQUEST),
);

const createExportP2POutReversals = apiModelOfflineCreateReducer(
  EXPORT_P2P_OUT_REVERSAL,
  { deserializer: deserializeExportP2POutReversal },
);
const deleteExportP2POutReversals = apiModelOfflineDeleteReducer(
  EXPORT_P2P_OUT_REVERSAL,
  { deserializer: deserializeExportP2POutReversal },
);
const fetchExportP2POutReversals = apiModelOfflineFetchReducer(
  EXPORT_P2P_OUT_REVERSAL,
  { deserializer: deserializeExportP2POutReversal },
);

const exportP2POutReversals = reduceReducers(
  createExportP2POutReversals,
  deleteExportP2POutReversals,
  fetchExportP2POutReversals,
);

const holdings = apiModelOfflineFetchReducer(NLIS_HOLDINGS, {
  deserializer: deserializeNlisHolding,
  clearOnRequest: true,
  finishLoadingAfterChanges: true,
});

const nlisFiles = combineReducers({
  takeFiles,
  sellFiles,
  takeReversals,
  sellReversals,
  sightingFiles,
  p2pFiles,
  p2pReversals,
  exportP2PInFiles,
  exportP2PInReversals,
  exportP2POutFiles,
  exportP2POutReversals,
  holdings,
});

export default nlisFiles;
